import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { filter, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { of, shareReplay, Subject, withLatestFrom } from 'rxjs';
import { BazisFormService } from '@bazis/form/services/form.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BazisConfigurationService, SHARE_REPLAY_SETTINGS } from '@bazis/configuration.service';
import { BazisEntityService } from '@bazis/shared/services/entity.service';
import { BazisEditEntityDefaultComponent } from '@bazis/form/components/helpers/edit-entity-default.component';
import { FormEventService } from '@pages/shared/services/form-events.service';
import { EntData } from '@bazis/shared/models/srv.types';

@Component({
    selector: 'app-edit-entity-default',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [BazisFormService],
    standalone: false,
})
export class AppEditEntityDefaultComponent extends BazisEditEntityDefaultComponent {
    protected _beforeSaveState = null;

    changes$ = this.formService.haveUnsavedChanges$.pipe(
        withLatestFrom(this.formService.entity$),
        filter(
            ([changes, entity]) =>
                changes &&
                entity &&
                (!this.statusToSaveByButton ||
                    entity.$snapshot.status.id !== this.statusToSaveByButton),
        ),
        tap((v) => {
            this.setBeforeState();
        }),
        switchMap(() => this.formService.save()),
        tap((v) => {
            this.sendFormEvent(v);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    syncChanges$ = this.formService.syncChanges$.pipe(
        withLatestFrom(this.formService.entity$),
        filter(
            ([canChange, entity]) =>
                canChange &&
                this.statusToSaveByButton &&
                entity &&
                entity.$snapshot.status.id !== this.statusToSaveByButton,
        ),

        tap((v) => {
            this.setBeforeState();
        }),
        switchMap(() => this.formService.save()),

        tap((v) => {
            this.sendFormEvent(v);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    saveByButton$ = this.forceSave$.pipe(
        filter((v) => !!v),
        tap(() => {
            this.formService.form.updateValueAndValidity();
        }),
        withLatestFrom(this.formService.form$),
        mergeMap(([save, form]) =>
            form.$config.id ? this.formService.haveUnsavedChanges$.pipe(take(1)) : of(true),
        ),
        filter((needSave) => {
            if (!needSave) this.forceSave$.next(false);

            return !!needSave;
        }),
        tap((v) => {
            this.setBeforeState();
        }),
        switchMap(() => this.formService.save()),
        tap((v) => {
            this.sendFormEvent(v);
            this.forceSave$.next(false);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    unconditionalSaveByButton$ = this.forceSave$.pipe(
        filter((v) => !!v),
        tap(() => {
            this.formService.form.updateValueAndValidity();
        }),
        withLatestFrom(this.formService.form$),
        tap((v) => {
            this.setBeforeState();
        }),
        switchMap(() => this.formService.save()),
        tap((v) => {
            this.sendFormEvent(v);
            this.forceSave$.next(false);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    constructor(
        protected router: Router,
        protected route: ActivatedRoute,
        public formService: BazisFormService,
        protected configurationService: BazisConfigurationService,
        protected entityService: BazisEntityService,
        protected formEventService: FormEventService,
    ) {
        super(router, route, formService, configurationService, entityService);
    }

    setBeforeState() {
        this._beforeSaveState = { id: this.formService.form.$config.id };
    }

    sendFormEvent(entity: EntData) {
        if (!entity) {
            if (this._beforeSaveState.id) {
                this.formEventService.formEvent$.next({
                    type: 'removed',
                    entityDescription: {
                        id: this._beforeSaveState.id,
                        type: this.formService.form.$config.entityType,
                    },
                });
            }
            return;
        }

        this.formEventService.formEvent$.next({
            type: this._beforeSaveState.id ? 'updated' : 'created',
            entityDescription: {
                id: entity.id,
                type: this.formService.form.$config.entityType,
            },
            entity,
        });
    }
}
